/**
 * Home. The map is the main input surface; the bottom rail is the "what do I do now" row.
 * One dominant recommended action per session, as the art direction requires.
 */

import { useEffect, useState } from 'react'
import { cents } from '@ashport/shared/formulas'
import { A } from '../art'
import { Icon } from '../ui/Icon'
import { Art, Btn, Col, Face, Meter, Panel, PanelHead, Rail, Row, Stack, Stat } from '../ui/Kit'
import { sz, useViewport, viewport } from '../ui/viewport'
import type { Route } from '../shell/routes'
import { CityMap } from './CityMap'
import { MorningLineCard } from './MorningLine'
import { useSession } from '../game/session'
import { districts, clock, exact, useGame } from '../game/state'
import { LineChoice } from '../screens/Families'
import { LINE_NAME } from '@ashport/shared'

/*
 * `SCANNER` and `BOSSES` were here, and both were mock data for systems that do not exist.
 *
 * `SCANNER` named three people with a Heat percentage and a dollar bounty each. Two of the three
 * are canon characters wearing somebody else's portrait, the third is invented, and there is no
 * bounty table in any migration. `BOSSES` was a four-row standings board with a Legend total per
 * row, on a game where nothing writes Legend at all.
 *
 * Both are deleted rather than corrected, for the reason the Family roster card was deleted from
 * this rail in Phase 5: the rail is "what do I do now", and a card for a system with no route is
 * not an action. The Ledger screen now carries the real standings, out of `legend_board`.
 */
function BeatPanel() {
  const { beat, now } = useGame()
  const d = districts().find((x) => x.id === beat.district)!
  return (
    <Panel tone="danger" style={{ flex: 'none' }}>
      <PanelHead title="The Beat" icon="warn" accent="var(--heat)" />
      <div style={{ padding: '10px 11px 12px' }}>
        <div
          className="t-head"
          style={{
            fontSize: 21,
            color: 'var(--heat)',
            letterSpacing: '.1em',
            textAlign: 'center',
            padding: '7px 0',
            background: 'linear-gradient(90deg, transparent, rgba(240,90,50,.16), transparent)',
            border: '1px solid rgba(240,90,50,.3)',
          }}
        >
          {d.name}
        </div>
        <Row style={{ marginTop: 11, justifyContent: 'space-around' }}>
          <Stat label="Payout Boost" value={`+${Math.round(beat.payout * 100)}%`} c="var(--dirty)" align="center" />
          <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--line)' }} />
          <Stat label="Heat Risk" value={`x${beat.heatMult}`} c="var(--arrest)" align="center" />
        </Row>
        <Row gap={6} style={{ marginTop: 10, justifyContent: 'center' }}>
          <Icon n="clock" s={12} c="var(--ink-dim)" />
          <span className="t-label" style={{ fontSize: sz(9) }}>Ends in</span>
          <span className="t-mono" style={{ color: 'var(--ink-hi)', fontSize: 12 }}>
            {clock((beat.endsAt - now) / 1000)}
          </span>
        </Row>
        <p style={{ margin: '10px 0 0', fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
          Cops are flooding {d.name}. Higher rewards, but double the Heat if you get caught.
        </p>
        {/*
          "View Beat Details" WAS HERE and it had no `onClick`. Deleted rather than wired, because
          there is no Beat detail screen to wire it to and there is nothing a detail screen would
          say that this panel does not already: the district, the payout multiplier, the Heat
          multiplier and the time left are the whole of the Beat. A button that opens a page
          repeating four numbers is a button that teaches a player their taps are ignored.
        */}
      </div>
    </Panel>
  )
}

/**
 * The Wash. The only bridge between the two currencies, and the single best economic dial there is.
 *
 * Every number here arrives priced against this player at this instant, from `GET /api/money`, and
 * the commit sends the published rate back so the server can refuse if it has moved. The client
 * does exactly one piece of arithmetic, `amount * rate`, on two numbers the server sent, which is
 * the only kind of arithmetic a client is allowed to do.
 *
 * This card previously read "Heat increase +6%". **Laundering generates no Heat at all.** Heat is
 * an INPUT to the rate, not an output of the wash, and the confusion is worth naming: what costs
 * you is being hot when you wash, not washing.
 */
function LaunderCard() {
  const money = useSession((s) => s.money)
  const busy = useSession((s) => s.busy)
  const launder = useSession((s) => s.launder)
  const loadMoney = useSession((s) => s.loadMoney)
  const [ask, setAsk] = useState('')

  useEffect(() => {
    void loadMoney()
  }, [loadMoney])

  const dirty = money?.dirty ?? cents(0)
  const rateBp = money?.rateBp ?? 0
  const typed = Number(ask)
  const amount = cents(
    Number.isFinite(typed) && typed > 0 ? Math.min(Math.round(typed * 100), dirty) : dirty,
  )
  const out = cents(Math.floor(amount * (rateBp / 10_000)))
  const enough = amount >= (money?.minBatch ?? 0)

  return (
    <Panel>
      <PanelHead
        title="Launder Cash"
        icon="clean"
        accent="var(--clean)"
        sub={money ? `tier ${money.launderTier}` : undefined}
      />
      <div style={{ padding: '9px 11px 11px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Row gap={10}>
          <Art
            src={A('items.materials')}
            alt="Back-room laundering setup"
            scrim="none"
            style={{ width: 58, height: 58, flex: 'none', border: '1px solid var(--line)' }}
          />
          <div>
            <div className="t-label" style={{ fontSize: sz(9) }}>You will receive</div>
            <div className="t-num" style={{ fontSize: 21, color: 'var(--clean)' }}>{exact(out)}</div>
          </div>
        </Row>
        <div className="t-label" style={{ fontSize: sz(9), marginTop: 10 }}>
          Dirty cash to launder
        </div>
        <input
          value={ask}
          onChange={(e) => setAsk(e.target.value.replace(/[^0-9]/g, ''))}
          placeholder={String(Math.floor(dirty / 100))}
          style={{
            marginTop: 3,
            padding: '5px 8px',
            width: '100%',
            background: '#05080a',
            border: '1px solid var(--line)',
            color: 'var(--dirty)',
            font: '400 13px/1.2 var(--f-mono, var(--f-body))',
            outline: 'none',
          }}
        />
        <div style={{ marginTop: 7 }}>
          <Meter v={rateBp} max={10_000} a="var(--brass-deep)" b="var(--clean)" h={4} />
        </div>
        <Row style={{ marginTop: 8, justifyContent: 'space-between' }}>
          <span style={{ fontSize: sz(10.5), color: 'var(--ink-mid)' }}>Conversion rate</span>
          <span className="t-mono" style={{ fontSize: 11, color: 'var(--gold)' }}>
            {(rateBp / 100).toFixed(1)}%
          </span>
        </Row>
        <Row style={{ justifyContent: 'space-between' }}>
          <span style={{ fontSize: sz(10.5), color: 'var(--ink-mid)' }}>Cost of your Heat</span>
          <span className="t-mono" style={{ fontSize: 11, color: 'var(--heat)' }}>
            {money && money.heatPenaltyBp > 0 ? `- ${(money.heatPenaltyBp / 100).toFixed(1)}%` : 'none'}
          </span>
        </Row>
        <div style={{ flex: 1 }} />
        <Btn
          v="safe"
          size="sm"
          full
          disabled={busy || !enough}
          onClick={() => void launder(amount)}
          style={{ marginTop: 9 }}
        >
          {enough ? 'Launder Cash' : 'Bring a hundred at least'}
        </Btn>
        <TierLadder />
        <FenceStall />
      </div>
    </Panel>
  )
}

/**
 * THE FENCE, which is the only way contraband leaves a player's hands.
 *
 * Crimes drop contraband. `content/items/contraband.json` ships 22 kinds, `dropPoolFor` hands them
 * out on every clean success, and `Market.tsx` says outright that contraband is listed in neither
 * market because "it converts to dirty cash at the NPC fence on the Money screen". There is no
 * Money screen. `POST /api/money/fence` exists, `FenceOffer[]` has always ridden on the same read
 * this panel already makes, the store has always had `fence()`, and **nothing called it.**
 *
 * So the loop had no exit: a player earned contraband, saw it in their pocket, and could never turn
 * it into anything. That is worse than a missing feature. A missing feature is absent; a dead end
 * is a promise the game made and did not keep.
 *
 * The whole stack, never a partial: the fee curve moves with Heat, so a player who sells half now
 * and half later at a different Heat gets two different prices for one pile and cannot tell why.
 * `expectFeeBp` is the price lock, sent with the tap and refused by the server if the fee moved.
 */
function FenceStall() {
  const money = useSession((s) => s.money)
  const busy = useSession((s) => s.busy)
  const fence = useSession((s) => s.fence)
  const stock = money?.fence ?? []

  return (
    <div style={{ marginTop: 11, borderTop: '1px solid var(--line-soft)', paddingTop: 9 }}>
      <Row style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span className="t-label" style={{ fontSize: sz(9) }}>The fence</span>
        <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--heat)' }}>
          {/* The fee is the Heat cost, stated before the tap like every other price. */}
          {money ? `fee ${(money.fenceFeeBp / 100).toFixed(1)}%` : ''}
        </span>
      </Row>
      {stock.length === 0 ? (
        <div style={{ fontSize: sz(10.5), color: 'var(--ink-faint)', marginTop: 5, lineHeight: 1.4 }}>
          Nothing to move. Contraband comes off crimes, and this is where it turns into cash.
        </div>
      ) : (
        <Col gap={5} style={{ marginTop: 5 }}>
          {stock.map((f) => (
            <Row key={f.slotId} gap={8} style={{ alignItems: 'center' }}>
              <Art
                src={A(f.artId)}
                alt={f.name}
                scrim="none"
                style={{ width: 26, height: 26, flex: 'none', border: '1px solid var(--line)' }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11.5, color: 'var(--ink)' }}>
                  {f.name}
                  {f.qty > 1 ? ` x${f.qty}` : ''}
                </div>
                <div className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-faint)' }}>
                  {exact(f.unitBase)} each before the fee
                </div>
              </div>
              <Btn
                v="gold"
                size="sm"
                disabled={busy}
                onClick={() => void fence(f.slotId, f.qty)}
              >
                {exact(f.netAll)}
              </Btn>
            </Row>
          ))}
        </Col>
      )}
    </div>
  )
}

/**
 * THE LAUNDERING LADDER, which was computed on every read and shown to nobody.
 *
 * `GET /api/money` has always returned `tiers: TierOffer[]`, priced against this player at this
 * instant: the cost, the bonus, the Heat floor it drags with it, whether they own it, whether they
 * can afford it, and an authored reason when they cannot. `buyTier` has always existed on the
 * store, `POST /api/money/tier` has always existed on the server, and **no component called
 * either**, so the single largest permanent upgrade in the economy was unreachable to a player.
 *
 * Three rungs against a base rate of 55 percent. Tier 3 is 18,000,000 clean and 30 points of rate,
 * which is the difference between the early game and the late one.
 *
 * Everything printed here is priced by the server and none of it is derived on the client. The
 * cost is on the button before the tap, which is the rule; the Heat floor is printed beside it,
 * because a rung that raises the floor you can never decay below is a cost as real as the money
 * and it is the one a player would otherwise discover afterwards.
 */
function TierLadder() {
  const money = useSession((s) => s.money)
  const busy = useSession((s) => s.busy)
  const buyTier = useSession((s) => s.buyTier)
  const tiers = money?.tiers ?? []
  if (tiers.length === 0) return null

  return (
    <div style={{ marginTop: 11, borderTop: '1px solid var(--line-soft)', paddingTop: 9 }}>
      <div className="t-label" style={{ fontSize: sz(9) }}>The ladder</div>
      <Col gap={5} style={{ marginTop: 5 }}>
        {tiers.map((t) => (
          <Row key={t.tier} gap={8} style={{ alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Row gap={6} style={{ alignItems: 'baseline' }}>
                <span style={{ fontSize: 11.5, color: t.owned ? 'var(--clean)' : 'var(--ink)' }}>
                  {t.name}
                </span>
                <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--gold)' }}>
                  +{(t.bonusBp / 100).toFixed(0)}%
                </span>
              </Row>
              <div style={{ fontSize: sz(10), color: 'var(--ink-faint)', lineHeight: 1.35 }}>
                {/*
                  * The floor is stated because it is the half of the price a player pays later.
                  * Every rung raises the Heat you can never decay below.
                  */}
                {t.owned
                  ? `Held. Heat never falls below ${t.heatFloor}.`
                  : (t.lockedReason ?? `Heat floor rises to ${t.heatFloor}`)}
              </div>
            </div>
            {t.owned ? (
              <span className="t-head" style={{ fontSize: sz(9.5), letterSpacing: '.12em', color: 'var(--clean)' }}>
                HELD
              </span>
            ) : (
              <Btn
                v={t.affordable && t.lockedReason === null ? 'gold' : 'ghost'}
                size="sm"
                disabled={busy || !t.affordable || t.lockedReason !== null}
                onClick={() => void buyTier(t.tier)}
              >
                {exact(t.cost)}
              </Btn>
            )}
          </Row>
        ))}
      </Col>
    </div>
  )
}

/**
 * THE TILL, which filled up and stopped and told nobody.
 *
 * A front business accrues clean income against `BUSINESS_ACCRUAL_CAP_HOURS`, sixty hours, and
 * then stops. `GET /api/gear` has always returned `till`, `tillHours` and `tillCapped`, computed on
 * every read; `collectFronts` has always been on the store; `POST /api/gear/collect` has always
 * been on the server; and **no component called any of them.** A player who bought a front earned
 * money into a jar they could not open, and the jar filled and overflowed silently.
 *
 * `tillCapped` is the important field and it is why this card exists rather than a bare button.
 * The cap punishes five days away rather than two, deliberately, and a player can only make that
 * trade if they can see it. So the card says the till is full when it is full, in those words.
 */
function FrontsCard() {
  const gear = useSession((s) => s.gear)
  const busy = useSession((s) => s.busy)
  const collectFronts = useSession((s) => s.collectFronts)
  const loadGear = useSession((s) => s.loadGear)

  useEffect(() => {
    void loadGear()
  }, [loadGear])

  // No fronts, no card. An empty panel about a thing you do not own is noise on the one screen
  // whose whole job is "what do I do now".
  if (!gear || gear.fronts === 0) return null

  return (
    <Panel>
      <PanelHead
        title="The Till"
        icon="clean"
        accent="var(--clean)"
        sub={`${gear.fronts} of ${gear.ownCap} fronts`}
      />
      <div style={{ padding: '9px 11px 11px' }}>
        <div className="t-label" style={{ fontSize: sz(9) }}>Waiting to be collected</div>
        <div className="t-num" style={{ fontSize: 21, color: 'var(--clean)' }}>{exact(gear.till)}</div>
        <div
          style={{
            fontSize: sz(10.5),
            marginTop: 4,
            lineHeight: 1.4,
            color: gear.tillCapped ? 'var(--heat)' : 'var(--ink-faint)',
          }}
        >
          {gear.tillCapped
            ? `Full. It stopped earning at ${gear.accrualCapHours} hours and is losing you money every hour it sits.`
            : `${gear.tillHours} hours in. It stops earning at ${gear.accrualCapHours}.`}
        </div>
        <Btn
          v={gear.tillCapped ? 'gold' : 'safe'}
          size="sm"
          full
          disabled={busy || gear.till <= 0}
          onClick={() => void collectFronts()}
          style={{ marginTop: 9 }}
        >
          {gear.till > 0 ? 'Empty The Till' : 'Nothing in it yet'}
        </Btn>
      </div>
    </Panel>
  )
}

/**
 * The three ways out of Heat, priced against this player.
 *
 * The Precinct Captain is the largest deliberate dirty sink in the game and his price escalates
 * with every clearing of the Era and never resets inside one, so the price is published before the
 * tap and the commit refuses if it has moved.
 */
function CoolOffCard() {
  const heat = useSession((s) => s.heat)
  const busy = useSession((s) => s.busy)
  const coolOff = useSession((s) => s.coolOff)
  const loadHeat = useSession((s) => s.loadHeat)
  const [points, setPoints] = useState('10')

  useEffect(() => {
    void loadHeat()
  }, [loadHeat])

  const available = heat?.maxPoints ?? 0
  const want = Math.max(1, Math.min(Number(points) || 1, Math.max(1, available)))
  const cost = cents((heat?.costPerPoint ?? 0) * want)
  const bribe = heat?.methods.find((m) => m.method === 'bribe')
  const legit = heat?.methods.find((m) => m.method === 'go_legit')
  const lie = heat?.methods.find((m) => m.method === 'lie_low')
  const back = heat?.methods.find((m) => m.method === 'back_to_street')

  return (
    <Panel tone="danger">
      <PanelHead
        title="Cool Off"
        icon="heat"
        accent="var(--heat)"
        sub={heat ? `${heat.clearsThisEra} cleared` : undefined}
      />
      <div style={{ padding: '9px 11px 11px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Row gap={8}>
          <Art
            src={A('heat.cooloff.bribe_official')}
            alt="An envelope changing hands in a stairwell"
            scrim="none"
            style={{ width: 58, height: 58, flex: 'none', border: '1px solid var(--line)' }}
          />
          <div style={{ minWidth: 0 }}>
            {/*
              At Heat 0 there is nothing to buy, so the card says so instead of quoting a price
              for a point that does not exist. A price on a disabled button is a number a player
              will plan against and then discover was never real.
            */}
            {available > 0 ? (
              <>
                <div className="t-label" style={{ fontSize: sz(9) }}>The Captain wants</div>
                <div className="t-num" style={{ fontSize: 19, color: 'var(--dirty)' }}>{exact(cost)}</div>
                <div style={{ fontSize: sz(10), color: 'var(--ink-dim)' }}>
                  {exact(cents(heat?.costPerPoint ?? 0))} a point
                  {heat && heat.ricoMultiplier > 1 ? ', 2.2x under RICO' : ''}
                </div>
              </>
            ) : (
              <>
                <div className="t-label" style={{ fontSize: sz(9) }}>The Captain</div>
                <div style={{ fontSize: 11.5, color: 'var(--ink-mid)', marginTop: 3, lineHeight: 1.4 }}>
                  There is nothing on you worth paying off.
                </div>
              </>
            )}
          </div>
        </Row>
        {available > 0 && (
          <Row gap={6} style={{ marginTop: 9 }}>
            <input
              value={points}
              onChange={(e) => setPoints(e.target.value.replace(/[^0-9]/g, ''))}
              style={{
                width: 62,
                padding: '5px 8px',
                background: '#05080a',
                border: '1px solid var(--line)',
                color: 'var(--ink)',
                font: '400 12px/1.2 var(--f-mono, var(--f-body))',
                outline: 'none',
                textAlign: 'right',
              }}
            />
            <span style={{ fontSize: sz(10.5), color: 'var(--ink-mid)' }}>
              points off, {available} on you
            </span>
          </Row>
        )}
        <Btn
          v="danger"
          size="sm"
          full
          disabled={busy || !bribe?.available}
          onClick={() => void coolOff('bribe', want)}
          style={{ marginTop: 8 }}
          title={bribe?.lockedReason ?? undefined}
        >
          Pay Him
        </Btn>
        <Row gap={6} style={{ marginTop: 6 }}>
          <Btn
            size="sm"
            full
            disabled={busy || !lie?.available}
            onClick={() => void coolOff('lie_low', 0)}
            title={lie?.lockedReason ?? undefined}
          >
            Lie Low
          </Btn>
          {/*
            ONE SLOT, TWO STATES. Run Legit and the way back out of it are the same decision seen
            from its two sides, and a card that showed both at once would offer a player a button
            they can never press. The server decides which is live: exactly one of the pair carries
            `available`, because each is the other's locked reason.
          */}
          {back?.available ? (
            <Btn
              size="sm"
              full
              disabled={busy}
              onClick={() => void coolOff('back_to_street', 0)}
              title={back.lockedReason ?? undefined}
            >
              Back to the Street
            </Btn>
          ) : (
            <Btn
              size="sm"
              full
              disabled={busy || !legit?.available}
              onClick={() => void coolOff('go_legit', 0)}
              title={legit?.lockedReason ?? undefined}
            >
              Run Legit
            </Btn>
          )}
        </Row>
        {/*
          WHAT IT IS COSTING, WHILE IT IS COSTING IT, and the panel never said so.
          Running legit takes 45 percent off every Job and every Crime for as long as it is on, and
          the card showed only the decay it buys. A player could take it on day one and spend an Era
          wondering why the numbers were small. The percentage comes off the payload rather than
          being typed here, so it cannot drift from the number the server actually applies.
        */}
        {back?.available && heat && (
          <div style={{ fontSize: sz(10), color: 'var(--heat)', marginTop: 6 }}>
            Running legit. Every job and crime pays {Math.round((heat.legitPayoutBp ?? 5500) / 100)} percent.
          </div>
        )}
        {/* The lock states its requirement rather than greying out in silence. */}
        {!back?.available && legit && !legit.available && (
          <div style={{ fontSize: sz(10), color: 'var(--ink-faint)', marginTop: 6 }}>
            {legit.lockedReason}
          </div>
        )}
        <div style={{ flex: 1 }} />
      </div>
    </Panel>
  )
}

/**
 * THE JOB CARD, AND IT USED TO BE THE LARGEST LIE ON THE FIRST SCREEN.
 *
 * It read nothing from the store. A brand new level 1 player with no money and no runs was told
 * they were on "Step 2 of 5" of a chain worth $312,000 at +18% Heat, over a `View Job` button with
 * no `onClick`, on the screen the game opens on. It survived directly beside the comment at the
 * top of this file that deleted `SCANNER` and `BOSSES` for exactly that.
 *
 * What replaces it is the same card reading `GET /api/jobs`: the chain the player is actually on,
 * the step they are actually at, the payout the server priced for them, and a button that goes to
 * the screen. `JobsPayload` has carried all of it since Phase 4.
 */
function JobCard({ go }: { go: (r: Route) => void }) {
  const jobs = useSession((s) => s.jobs)
  const loadJobs = useSession((s) => s.loadJobs)

  useEffect(() => {
    void loadJobs().catch(() => undefined)
  }, [loadJobs])

  /*
   * The chain to lead with is the one the player has gone furthest into, and the tie is broken by
   * the order the server sent, which is the order the Jobs screen renders. A "featured" chain
   * chosen by anything else would be a recommendation the game cannot justify.
   */
  const chains = (jobs?.chains ?? []).filter((c) => !c.comingSoon)
  const chain =
    chains.find((c) => c.jobs.some((j) => j.runs > 0)) ??
    chains.find((c) => c.jobs.some((j) => j.state !== 'locked')) ??
    chains[0]
  const step =
    chain?.jobs.find((j) => j.state !== 'locked' && j.mastery !== 'gold') ?? chain?.jobs[0]

  if (!chain || !step) {
    return (
      <Panel style={{ overflow: 'hidden' }}>
        <PanelHead title="The chains" icon="jobs" />
        <div style={{ padding: '11px 12px', fontSize: 11.5, color: 'var(--ink-dim)', lineHeight: 1.5 }}>
          The board has not come back yet.
        </div>
      </Panel>
    )
  }

  return (
    <Panel style={{ overflow: 'hidden' }}>
      <PanelHead title="Your chain" icon="jobs" sub={chain.label} />
      <Art src={A(chain.heroId)} alt={chain.title} style={{ height: 82, flex: 'none' }}>
        <div style={{ position: 'absolute', inset: 'auto 10px 7px', textShadow: '0 2px 8px #000, 0 0 18px #000' }}>
          <div
            className="t-head"
            style={{ fontSize: 14, color: 'var(--gold-hot)', letterSpacing: '.04em', lineHeight: 1.15 }}
          >
            {chain.title}
          </div>
        </div>
      </Art>
      <div style={{ padding: '9px 11px 11px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <p
          style={{
            margin: 0,
            fontSize: 11.5,
            color: 'var(--ink-mid)',
            lineHeight: 1.45,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {step.desc}
        </p>
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ink)' }}>
          Step <b style={{ color: 'var(--gold)' }}>{step.step} of {chain.jobs.length}</b>
          {step.lockedReason !== null && (
            <span style={{ color: 'var(--ink-dim)' }}> &middot; {step.lockedReason}</span>
          )}
        </div>
        <div style={{ marginTop: 6 }}>
          {/* Runs toward the next mastery tier, which is the only progress a step actually has. */}
          <Meter
            v={step.runs}
            max={step.masteryNext ?? Math.max(step.runs, 1)}
            a="var(--brass-deep)"
            b="var(--gold)"
            h={4}
          />
        </div>
        <div style={{ flex: 1 }} />
        <Row style={{ marginTop: 9, justifyContent: 'space-between' }}>
          <div>
            <div className="t-label" style={{ fontSize: sz(8.5) }}>Pays</div>
            <div className="t-num" style={{ fontSize: 14, color: 'var(--dirty)' }}>{exact(step.payout)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            {/* Jobs raise NO Heat. The old card printed "+18%", which is not a thing a job does. */}
            <div className="t-label" style={{ fontSize: sz(8.5) }}>Costs</div>
            <div className="t-num" style={{ fontSize: 14, color: 'var(--energy)' }}>{step.energy} energy</div>
          </div>
        </Row>
        <Btn v="gold" size="sm" full style={{ marginTop: 9 }} onClick={() => go('jobs')}>
          Open the board
        </Btn>
      </div>
    </Panel>
  )
}

/*
 * The Family roster card came off the Home action rail in Phase 5. The rail is 'what do I do
 * now', a roster is not an action, and the Families screen owns it. Its mock data went with
 * it. Phase 6 builds the real one.
 */

/**
 * The family room, on Home.
 *
 * The SAME chat the Families screen shows, from the same slice, because there is exactly one of
 * them. This panel used to render five hand-written lines from named characters at 10:42 PM, and
 * once family chat became real that turned into a fabricated conversation sitting beside a live
 * one. A player would have had no way to tell which was which.
 *
 * It reads and posts and does not hold the socket: the Families screen owns the room's lifetime,
 * so a player sitting on Home sees whatever the last read produced and gets live frames only
 * while the other screen is open. That is a deliberate limit rather than an oversight. Opening a
 * second socket from here would double every family's connection count for a panel nobody is
 * looking at, and hibernation discipline is the difference between a ten dollar line item and a
 * hundred and thirty eight dollar one.
 */
function ChatPanel() {
  const family = useSession((s) => s.family)
  const busy = useSession((s) => s.busy)
  const loadFamily = useSession((s) => s.loadFamily)
  const postFamilyChat = useSession((s) => s.postFamilyChat)
  const [draft, setDraft] = useState('')

  useEffect(() => {
    void loadFamily().catch(() => undefined)
  }, [loadFamily])

  const lines = family?.chat ?? []
  const inCrew = family?.overview !== null && family?.overview !== undefined

  const send = () => {
    const body = draft.trim()
    if (body === '' || busy || !inCrew) return
    setDraft('')
    void postFamilyChat(body)
  }

  return (
    <Panel style={{ flex: '1 1 0' }}>
      <PanelHead
        title="Family Chat"
        icon="users"
        right={
          inCrew ? (
            <Row gap={5}>
              <Icon n="users" s={12} c="var(--ink-dim)" />
              <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--dirty)' }}>
                {family?.overview?.memberCount ?? 0}
              </span>
            </Row>
          ) : undefined
        }
      />
      <div className="scroll-y" style={{ flex: 1, padding: '8px 10px' }}>
        {!inCrew ? (
          <div style={{ fontSize: 11.5, color: 'var(--ink-dim)', lineHeight: 1.5 }}>
            You are not at anybody's table yet. Nobody gets far in this city on their own.
          </div>
        ) : lines.length === 0 ? (
          <div style={{ fontSize: 11.5, color: 'var(--ink-dim)', lineHeight: 1.5 }}>
            Nobody has said anything yet.
          </div>
        ) : (
          <Col gap={9}>
            {lines.slice(-12).map((c) => (
              <Row key={c.seq} gap={8} style={{ alignItems: 'flex-start' }}>
                <Face src={A(c.portraitId)} alt={c.who} size={26} round />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Row gap={6}>
                    <span
                      style={{
                        fontSize: 11.5,
                        color: c.me ? 'var(--gold)' : 'var(--ink-hi)',
                        fontWeight: 500,
                      }}
                    >
                      {c.who}
                    </span>
                    <span className="t-mono" style={{ fontSize: sz(9.5), color: 'var(--ink-faint)' }}>
                      {new Date(c.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </span>
                  </Row>
                  <div style={{ fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.45 }}>{c.body}</div>
                </div>
              </Row>
            ))}
          </Col>
        )}
      </div>
      <Row gap={0} style={{ padding: '7px 8px', borderTop: '1px solid var(--line-soft)' }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') send()
          }}
          disabled={!inCrew || busy}
          placeholder={inCrew ? 'Type a message...' : 'Join a crew first'}
          style={{
            flex: 1,
            padding: '7px 9px',
            background: '#05080a',
            border: '1px solid var(--line)',
            color: 'var(--ink)',
            font: '400 12px/1 var(--f-body)',
            outline: 'none',
            opacity: inCrew ? 1 : 0.5,
          }}
        />
        <button
          onClick={send}
          disabled={!inCrew || busy || draft.trim() === ''}
          aria-label="Send"
          style={{
            width: viewport().touch ? 44 : 33,
            height: viewport().touch ? 44 : 31,
            display: 'grid',
            placeItems: 'center',
            background: 'linear-gradient(180deg,#2a353d,#151d22)',
            border: '1px solid var(--line)',
            borderLeft: 'none',
            cursor: inCrew && !busy ? 'pointer' : 'default',
            opacity: inCrew && !busy && draft.trim() !== '' ? 1 : 0.5,
          }}
        >
          <Icon n="send" s={14} c="var(--gold)" />
        </button>
      </Row>
    </Panel>
  )
}

/**
 * The vouch that made you, or the fact that nobody has.
 *
 * This panel named Vincenzo "The Chair" Aiello unconditionally, for every player, from the first
 * second of the game. Sponsorship became real in Phase 6, so an unsponsored Associate was being
 * told they had a patron they had never met, on the screen the game opens on, next to a favour
 * meter reading a number with nothing behind it.
 *
 * "Time to first sponsor" is the north-star onboarding metric in GAME-DESIGN 6.3, so the honest
 * empty state is the more useful one anyway: it says the thing the player has to go and do.
 */
function SponsorPanel() {
  const { player } = useGame()
  const family = useSession((s) => s.family)
  const mine = family?.sponsor.mine ?? null
  const incoming = family?.sponsor.incoming ?? null

  return (
    <Panel tone="gold">
      <PanelHead title="Your Sponsor" icon="rank" accent="var(--gold)" />
      <div style={{ padding: '9px 11px 11px' }}>
        {mine !== null ? (
          <Row gap={10}>
            <Face src={A('characters.ranks.capo_silhouette')} alt={mine.sponsorName} size={52} ring />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="t-head" style={{ fontSize: 12.5, color: 'var(--ink-hi)', letterSpacing: '.06em' }}>
                {mine.sponsorName}
              </div>
              <div className="t-label" style={{ fontSize: sz(9), marginTop: 3 }}>
                Witnessed by <span style={{ color: 'var(--gold)' }}>{mine.witnessName || 'nobody yet'}</span>
              </div>
            </div>
          </Row>
        ) : incoming !== null ? (
          <div>
            <div className="t-head" style={{ fontSize: 12, color: 'var(--gold)', letterSpacing: '.06em' }}>
              Somebody has vouched for you
            </div>
            <p style={{ margin: '6px 0 0', fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
              {incoming.blurb} You learn the name when you accept.
            </p>
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-dim)', lineHeight: 1.5 }}>
            Nobody has vouched for you yet. Nothing above Associate exists without one.
          </p>
        )}
        <Row style={{ marginTop: 9, justifyContent: 'space-between' }}>
          <span className="t-label" style={{ fontSize: sz(9) }}>Next rank</span>
          <span className="t-head" style={{ fontSize: 11.5, color: 'var(--gold)', letterSpacing: '.13em' }}>
            {player.nextRank}
          </span>
        </Row>
      </div>
    </Panel>
  )
}

/**
 * WHAT TO DO NOW. The panel that replaces a gate. DESIGN-DELTAS 1t.4 and 1t.12.
 *
 * No route in this game is gated: thirteen are on the rail from second one and none of them is
 * locked, because CCP killed EVE's linear tutorial specifically to stop players getting stuck on a
 * step, and because 1e.6 already ruled that this game shows a player what is out of reach rather
 * than hiding it. The cost of that decision is that the first screen has to carry the ordering
 * instead, and this panel is that ordering.
 *
 * **THE SERVER DECIDES EVERY TICK.** `docs/PHASE-6B.md` section 7: a beat is satisfied by a
 * predicate evaluated on the server against real player state, never a client assertion. There is
 * no endpoint that says "I did that". A player who runs a job for their own reasons has satisfied
 * the first beat, and this panel finds that out by reading, exactly like everything else.
 *
 * **IT ANSWERS ONE QUESTION AT TWO SCALES.** The beats are the first session; the rank checklist
 * underneath is the first week. They are one panel because they are one question, and the rank
 * half stays after the beats are gone, which is what stops this being a tutorial that vanishes and
 * leaves a hole.
 */
function NextPanel({ go }: { go: (r: Route) => void }) {
  const next = useSession((s) => s.next)
  const busy = useSession((s) => s.busy)
  const loadNext = useSession((s) => s.loadNext)
  const dismiss = useSession((s) => s.dismissFirstNight)
  const drawLine = useSession((s) => s.drawLine)

  useEffect(() => {
    void loadNext().catch(() => undefined)
  }, [loadNext])

  if (!next) return null
  const fn = next.firstNight
  const showBeats = !fn.dismissed && !fn.finished
  const req = next.rank.requirements

  return (
    <Panel tone="gold" style={{ flex: 'none' }}>
      <PanelHead
        title={showBeats ? 'The first night' : 'What next'}
        icon="rank"
        accent="var(--gold)"
        sub={showBeats ? `${fn.done} of ${fn.total}` : undefined}
      />
      <div style={{ padding: '9px 11px 11px' }}>
        {showBeats && (
          <>
            {fn.beats.map((b) => {
              const current = b.id === fn.currentId
              return (
                <div key={b.id} style={{ marginBottom: current ? 9 : 4 }}>
                  <Row gap={7} style={{ alignItems: 'baseline' }}>
                    <Icon
                      n={b.done ? 'check' : 'chev'}
                      s={11}
                      c={b.done ? 'var(--clean)' : current ? 'var(--gold)' : 'var(--ink-dim)'}
                    />
                    <span
                      style={{
                        fontSize: 11.5,
                        color: b.done ? 'var(--ink-dim)' : current ? 'var(--ink-hi)' : 'var(--ink-mid)',
                        textDecoration: b.done ? 'line-through' : undefined,
                      }}
                    >
                      {b.title}
                    </span>
                  </Row>
                  {/*
                    ONLY THE CURRENT BEAT CARRIES ITS COPY. Paradox cut Crusader Kings III's 67
                    sequential boxes to roughly a third, and Nielsen Norman measured that reading
                    a tutorial made the same tasks feel HARDER. Nine paragraphs at once is the
                    thing both of those findings are about.
                  */}
                  {current && (
                    <>
                      <p
                        style={{
                          margin: '5px 0 0 18px',
                          fontSize: 11.5,
                          color: 'var(--ink-mid)',
                          lineHeight: 1.5,
                        }}
                      >
                        {b.copy}
                      </p>
                      <div style={{ marginLeft: 18, marginTop: 7 }}>
                        <Btn v="gold" size="sm" onClick={() => go(b.where as Route)}>
                          Go there
                        </Btn>
                      </div>
                    </>
                  )}
                </div>
              )
            })}
            {/*
              THE WAY OUT, and it is server-recorded so it is honoured on every device.
              DESIGN-DELTAS 1t.11: nothing here is forced, and a list you cannot put away is.
            */}
            <button
              type="button"
              disabled={busy}
              onClick={() => void dismiss()}
              style={{
                marginTop: 8,
                background: 'none',
                border: 0,
                padding: viewport().touch ? '0 10px' : 0,
                minHeight: viewport().touch ? 44 : undefined,
                font: 'inherit',
                fontSize: sz(10.5),
                color: 'var(--ink-faint)',
                textDecoration: 'underline',
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              Put this away
            </button>
          </>
        )}

        {/*
          The rank checklist, which was built, correct, and buried inside the Families screen: an
          institution a new player has not joined. `GET /api/rank` has returned every condition
          with real have and need numbers since Phase 6.
        */}
        {/* PHASE 24. The Line, on the first screen, the moment Soldier lands. Once. */}
        {next.rank.lineOpen && <LineChoice busy={busy} onLine={(line) => void drawLine(line)} />}
        {next.rank.line !== '' && (
          <div className="t-mono" style={{ fontSize: sz(10), color: 'var(--brass-lit)', marginTop: 6 }}>
            Your Line: {LINE_NAME[next.rank.line]}.
          </div>
        )}
        {next.rank.nextRank !== null && (
          <div style={{ marginTop: showBeats ? 12 : 0, paddingTop: showBeats ? 9 : 0, borderTop: showBeats ? '1px solid var(--line-soft)' : undefined }}>
            <div className="t-label" style={{ fontSize: sz(9), marginBottom: 5 }}>
              To make {next.rank.nextRank}
            </div>
            <Col gap={3}>
              {req.map((r) => (
                <Row key={r.id} gap={7} style={{ alignItems: 'center' }}>
                  <Icon
                    n={r.met ? 'check' : 'chev'}
                    s={11}
                    c={r.met ? 'var(--clean)' : 'var(--ink-dim)'}
                  />
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 11,
                      color: r.met ? 'var(--ink-dim)' : 'var(--ink-mid)',
                    }}
                  >
                    {r.label}
                  </span>
                  {r.have !== null && r.need !== null && !r.met && (
                    <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-faint)' }}>
                      {r.have} / {r.need}
                    </span>
                  )}
                </Row>
              ))}
            </Col>
            <Btn size="sm" full style={{ marginTop: 8 }} onClick={() => go('families')}>
              {next.rank.canPromote ? 'Claim it' : 'The ladder'}
            </Btn>
          </div>
        )}
      </div>
    </Panel>
  )
}

/**
 * THE PROTECTION CHECKLIST, ON THE SCREEN A NEW PLAYER ACTUALLY OPENS.
 *
 * GAME-DESIGN 11.1 calls it "the clearest tutorial in the game" and says it "makes the end of
 * protection a choice they made rather than a thing that happened to them". It was built, correct,
 * and on the Fights screen, which is the one screen section 11 says the first ten minutes must not
 * show. `shieldUntil` has been on every `PlayerSnapshot` since Phase 1 and no component read it.
 *
 * **IT IS ITS OWN PANEL AND IT IS NOT PART OF "WHAT NEXT", WHICH IS A RULING.** DESIGN-DELTAS
 * 1t.12. These eight lines look exactly like a to-do list and are the opposite of one: every item
 * is something that ENDS a new player's immunity. Rendering them in the panel that says what to do
 * next would be instructing somebody to walk into the open, in the voice of the thing they trust
 * to tell them what to do. A choice is disclosed. A task is assigned.
 */
function ProtectionPanel() {
  const p = useSession((s) => s.pvp?.protection)
  const loadPvp = useSession((s) => s.loadPvp)

  useEffect(() => {
    void loadPvp().catch(() => undefined)
  }, [loadPvp])

  // Nothing to disclose once it is over. A permanent panel headed "you are in the open" on the
  // first screen, for the whole rest of the Era, is a warning that becomes wallpaper.
  if (!p || !p.standing) return null

  return (
    <Panel tone="safe" style={{ flex: 'none' }}>
      {/*
        "Any one of these ends it" was a PanelHead `sub`, which sits on the same line as the title.
        In a 214 pixel column that line is already spent on "Nobody can touch you", so 108 of the
        sub's pixels were cut: the player was shown "Any one of the" and left to guess whether the
        list was things they must do or things they must avoid. It is the sentence that makes the
        list legible, so it goes where there is room for it.
      */}
      <PanelHead title="Nobody can touch you" icon="lock" accent="var(--safe)" />
      <div style={{ padding: '7px 10px 0', fontSize: sz(10.5), color: 'var(--ink-dim)' }}>
        Any one of these ends it.
      </div>
      <Col gap={3} style={{ padding: '6px 10px 9px' }}>
        {p.conditions.map((c) => (
          <Row key={c.id} gap={7} style={{ alignItems: 'center' }}>
            <Icon n={c.met ? 'close' : 'chev'} s={11} c={c.met ? 'var(--arrest)' : 'var(--ink-dim)'} />
            <span style={{ fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.35 }}>{c.label}</span>
          </Row>
        ))}
      </Col>
    </Panel>
  )
}

/* ------------------------------------------------------------------- screen */

/*
 * A REFUSAL, ON THE SCREEN THAT CAUSED IT, and Home did not have one.
 *
 * Crimes, Jobs, Families, Fights and Market all show the server's refusal in voice. Home did not,
 * and Home is where the Morning Line, the Launder card and the Cool Off card live. All three
 * refuse for perfectly ordinary reasons on a new account: there is no dirty cash to launder, there
 * is no Heat worth paying off, the card is already claimed. The server answers each in written
 * English, `act()` in `session.ts` puts it in `refusal`, and NOTHING ON THIS SCREEN READ IT.
 *
 * What that looked like to a player: click, nothing happens, no reason, no error, no movement.
 * The only trace was a 409 in the browser console, which is where this was actually found.
 *
 * The other five screens each carry their own copy of this component and they have already
 * drifted apart. They should collapse onto one; this is deliberately not that change, because a
 * refactor across five screens is not what a player is waiting for.
 */
function Refusal({ text, onClose }: { text: string; onClose: () => void }) {
  return (
    <Panel tone="danger">
      <PanelHead
        title="No"
        icon="warn"
        accent="var(--arrest)"
        right={
          <button
            onClick={onClose}
            style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, lineHeight: 0 }}
            aria-label="Dismiss"
          >
            <Icon n="close" s={11} c="var(--ink-dim)" />
          </button>
        }
      />
      <p style={{ margin: 0, padding: '9px 11px 11px', fontSize: 12, color: 'var(--ink)' }}>{text}</p>
    </Panel>
  )
}

export function Home({ go }: { go: (r: Route) => void }) {
  const refusal = useSession((s) => s.refusal)
  const clearRefusal = useSession((s) => s.clearRefusal)
  const { stacked, width } = useViewport()

  /*
   * ONE COLUMN UNDER 1024px, since Phase 18, and the order is the burst session.
   *
   * GAME-DESIGN 1.2 reads three numbers without tapping (the top bar), reads one line of change
   * (the ticker), then taps into the hot district. So the map leads, with the Beat on it; "what
   * next" follows, because 1t.4 rules it carries the ordering; then the Beat's own panel; then the
   * five action cards one under another, because a sideways rail on a phone is a rail nobody
   * scrolls; then the chat and the sponsor, which are for the thirty-minute session. The chat
   * panel is given a height, because it is the one panel here that fills whatever it is put in.
   */
  if (stacked) {
    const mapH = Math.round(Math.min(Math.max(width - 16, 200) * 0.78, 460))
    return (
      <Stack>
        {refusal !== null && <Refusal text={refusal} onClose={clearRefusal} />}
        <div style={{ height: mapH, flex: 'none', display: 'flex' }}>
          <CityMap />
        </div>
        <NextPanel go={go} />
        <BeatPanel />
        <MorningLineCard />
        <LaunderCard />
        <CoolOffCard />
        <FrontsCard />
        <JobCard go={go} />
        <ProtectionPanel />
        <div style={{ height: 320, flex: 'none', display: 'flex', flexDirection: 'column' }}>
          <ChatPanel />
        </div>
        <SponsorPanel />
      </Stack>
    )
  }

  return (
    <Stack>
      {/*
        The left column SCROLLS, and it did not before Phase 13 because it held one panel.
        Three panels of natural height in a fixed 214 pixels overflowed the Beat's copy and put
        the protection checklist on top of it, which was visible in the first screenshot of the
        new Home and in nothing else.
      */}
      <Rail width={214}>
        {/*
          "What next" LEADS the column, above the Beat, and the order is the ruling. 1t.4: no route
          is gated, so this is the only thing on the first screen carrying the ordering, and a
          player who has to scroll to find it is a player who was not told.
        */}
        <NextPanel go={go} />
        <BeatPanel />
        <ProtectionPanel />
      </Rail>

      {/* centre: map on top, action rail beneath */}
      <Col gap={8} style={{ flex: 1, minWidth: 0 }}>
        {/* Directly above the rail that causes them, so the answer is beside the button. */}
        {refusal !== null && <Refusal text={refusal} onClose={clearRefusal} />}
        <CityMap />
        {/*
          FIVE CARDS THAT WOULD NOT FIT, AND SAID NOTHING ABOUT IT.

          This was `repeat(5, minmax(0, 1fr))`. The centre column is what is left after a 232px
          nav, a 214px left column and a 300px right column, so at a 1440 wide window each card got
          about ninety pixels. Every title ellipsised to "THE MORNI...", "LAUNDER C...", "COOL O...",
          "YOUR CHAI...", the launder button read "NG A HUNDRED AT LE", and the Cool Off body
          rendered one word per line. `minmax(0, 1fr)` is what let it: it permits a track to shrink
          to nothing, which is right for a cell that can reflow and wrong for a card with a fixed
          minimum legible width.

          Now each card gets at least 210px and the rail SCROLLS when they do not all fit. A rail
          the player can push sideways is worse than five cards that fit and better than five cards
          that cannot be read, and it degrades in the direction of the truth: the row is too narrow
          for what is on it, and it looks it.
        */}
        <div
          className="scroll-x"
          style={{
            display: 'grid',
            gridAutoFlow: 'column',
            gridAutoColumns: 'minmax(210px, 1fr)',
            gap: 8,
            height: 310,
            flex: 'none',
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          {/*
            The two server-wired cards lead, because they are the two a player can actually act
            on. The rail is "what do I do now", so the Family ROSTER came off it: a roster is not
            an action and the Families screen owns it.
          */}
          {/*
            The Morning Line LEADS the rail, because it is the only card on it that answers "why
            open the tab today" rather than "what do I do with what I already have".
          */}
          <MorningLineCard />
          <LaunderCard />
          <CoolOffCard />
          <FrontsCard />
          <JobCard go={go} />
        </div>
      </Col>

      {/* right column */}
      <Rail width={300}>
        <ChatPanel />
        <SponsorPanel />
      </Rail>
    </Stack>
  )
}
